import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { Modal } from "antd";
import { sortBy, uniqBy } from "lodash";
import moment from "moment";
import { LoadingStatusCode } from "../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../lib/utils/catchApiError";
import { IProduct, Offer } from "../../../lib/types";
import {
  fetchProductByIdRoute,
  fetchProlongOfferingRoute,
  fetchProductVersionsRoute,
  closeProductRoute,
  fetchHierarchyProductByIdRoute,
  cancelProductRoute,
  activeateProductRoute,
  changeProductStatusRoute,
  changeProlognOfferingRoute,
  getProductsBlockingTypeStatusRoute,
  changeProductQuantityRoute,
} from "../../../lib/routes/products";
import { buildQueryString } from "../../../lib/utils";
import { getRoute } from "../../../lib/routes";
import { IProductsBlockingTypeStatus } from "./types/index";

interface InitialState {
  product?: IProduct;
  productLoading: LoadingStatusCode;
  changeProductLoading: LoadingStatusCode; // Любое изменение продукта
  changeProductQuantityLoading: LoadingStatusCode;
  productVersions: IProduct[];
  productVersionsLoading: LoadingStatusCode;
  prolongOffering?: Offer;
  prolongOfferingLoading: LoadingStatusCode;
  prolongOfferingVersions: { [key: string]: Offer };
  prolongOfferingVersionsLoading: LoadingStatusCode;
  productsListLoading: LoadingStatusCode;
  productsList: IProduct[];
  productHierarchy?: IProduct[];
  productHierarchyLoading: LoadingStatusCode;
  productBlockingStatus: null[];
}

const initialState: InitialState = {
  productLoading: "idle",
  changeProductLoading: "idle",
  changeProductQuantityLoading: "idle",
  prolongOfferingVersions: {},
  prolongOfferingVersionsLoading: "idle",
  prolongOfferingLoading: "idle",
  productVersions: [],
  productVersionsLoading: "idle",
  productsListLoading: "idle",
  productsList: [],
  productHierarchyLoading: "idle",
  productBlockingStatus: [],
};

// Получение предложения для пролонгации
export const fetchProlongOffering = createAsyncThunk(
  "products/fetchProlongOffering",
  async (params: { id: number; onDate: string }) => {
    try {
      const response = axios.get(fetchProlongOfferingRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки предложения для пролонгации. Попробуйте позже",
      });
    }
  }
);

// Получение списка предложений для пролонгации версий продукта
export const fetchProlongOfferingVersions = createAsyncThunk(
  "products/fetchProlongOfferingVersions",
  async (params: { id: number; onDate: string }[]) => {
    try {
      const response = await Promise.all(
        params.map((paramItem) => axios.get<Offer>(fetchProlongOfferingRoute(paramItem)))
      );
      return params.reduce<any>(
        (result, { onDate }, idx) => ({ ...result, [onDate]: response[idx].data }),
        {}
      );
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки предложений для пролонгации. Попробуйте позже",
      });
    }
  }
);

// Получение предложения по id
export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (params: { productId: string; onDate: string }, { dispatch }) => {
    try {
      const response = axios.get<IProduct>(fetchProductByIdRoute(params));
      const { data } = await response;
      // dispatch(fetchProlongOffering({ id: 141, onDate: "2023-08-16T00:00:00+03:00" })); //Получение оффера для пролонгации для целей тестирования
      if (data.prolongOfferingId) {
        dispatch(fetchProlongOffering({ id: data.prolongOfferingId, onDate: data.fromDate }));
      }
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки продукта. Попробуйте позже",
      });
    }
  }
);

// Получение версий продукта
export const fetchProductVersions = createAsyncThunk(
  "products/fetchProductVersions",
  async (id: number | string, { dispatch }) => {
    try {
      const response = axios.get<IProduct[]>(fetchProductVersionsRoute(id));
      const { data } = await response;
      const productWithProlongOffering: IProduct[] = data.filter(({ prolongOfferingId }) =>
        Boolean(prolongOfferingId)
      );
      const requestData = data
        .filter(({ prolongOfferingId }) => Boolean(prolongOfferingId))
        .map(({ prolongOfferingId, fromDate }) => ({
          id: prolongOfferingId!,
          onDate: fromDate,
        }));
      if (requestData.length) {
        productWithProlongOffering.forEach((productItem) => {
          dispatch(fetchProlongOfferingVersions(requestData));
        });
      } else {
        dispatch(productsActions.cleanProlongOfferingVersions());
      }
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий продукта. Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия продукта
export const closeProduct = createAsyncThunk(
  "products/closeProduct",
  async (params: { id: number; fromDate: string }, { dispatch }) => {
    try {
      await axios.delete(closeProductRoute(params));
      Modal.success({ title: "Дата закрытия изменена" });
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения даты закрытия",
      });
    }
  }
);

// Отмена продукта
export const cancelProduct = createAsyncThunk(
  "products/cancelProduct",
  async (params: { id: number; fromDate?: string; hideSuccessModal?: boolean }) => {
    try {
      const { hideSuccessModal, ...requestBody } = params;
      await axios.delete(cancelProductRoute(requestBody));
      if (!hideSuccessModal) {
        Modal.success({ title: "Продукт отменен" });
      }
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка отмены продукта",
      });
    }
  }
);

// Активация продукта
export const activateProduct = createAsyncThunk(
  "products/activateProduct",
  async (params: {
    id: number;
    fromDate?: string;
    status: string;
    force?: boolean;
    activationCheck?: boolean;
    hideSuccessModal?: boolean;
  }) => {
    try {
      const { hideSuccessModal, ...requestBody } = params;
      await axios.patch(activeateProductRoute(requestBody));
      if (!hideSuccessModal) {
        Modal.success({ title: "Продукт активирован" });
      }
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка активации продукта",
      });
    }
  }
);

// Изменение статуса продукта
export const changeProductStatus = createAsyncThunk(
  "products/changeProductStatus",
  async (params: {
    id: number;
    fromDate?: string;
    status: string;
    force?: boolean;
    changeChildStatuses?: boolean;
    activationCheck?: boolean;
    hideSuccessModal?: boolean;
  }) => {
    try {
      const { hideSuccessModal, ...requestBody } = params;
      await axios.patch(changeProductStatusRoute(requestBody));
      if (!hideSuccessModal) {
        Modal.success({ title: "Статус продукта изменен" });
      }
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения статуса продукта",
      });
    }
  }
);

// Изменение количества экземпляров продукта
export const changeProductQuantity = createAsyncThunk(
  "products/changeProductQuantity",
  async (params: {
    id: number;
    fromDate?: string;
    quantity: number;
    force: boolean;
    hideSuccessModal?: boolean;
  }) => {
    try {
      const { hideSuccessModal, ...requestBody } = params;
      await axios.patch(changeProductQuantityRoute(requestBody));
      if (!hideSuccessModal) {
        Modal.success({ title: "Продукт обновлен" });
      }
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения количества экземпляров продукта",
        hideErrorModal: true,
      });
    }
  }
);

// Получение списка продуктов
export const fetchProductsByAggId = createAsyncThunk(
  "products/fetchProductsByAggId",
  async ({
    agreementsIds,
    futureDates = true,
    ...queryParams
  }: {
    agreementsIds: number[];
    futureDates: boolean;
    onDate?: string;
    includePastDates: boolean;
  }) => {
    try {
      let dataServices: IProduct[] = [];
      await Promise.all(
        agreementsIds.map(async (agreementId) => {
          const queryString = buildQueryString({
            agreementId,
            includeFutureDates: futureDates,
            extendedInfo: false,
            ...queryParams,
          });
          const url = getRoute("services-api", "services", "hierarchy", `search?${queryString}`);
          const response = await axios.get<IProduct[]>(url);
          const { data } = response;
          if (data && data.length !== 0) {
            dataServices = [...dataServices, ...data];
          }
        })
      );
      const sortedArr = sortBy(dataServices, (o) => o.fromDate);
      return uniqBy(sortedArr, "id");
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки продуктов. Попробуйте позже",
      });
    }
  }
);

// Получение иерархии продукта по id
export const fetchHierarchyProductById = createAsyncThunk(
  "products/fetchHierarchyProductById",
  async (params: {
    productId: string;
    agreementId: number;
    onDate: string;
    extendedInfo?: boolean;
  }) => {
    try {
      const response = axios.get<IProduct[]>(fetchHierarchyProductByIdRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки продукта. Попробуйте позже",
      });
    }
  }
);

// Изменение условий пролонгации
export const changeProlognOffering = createAsyncThunk(
  "products/changeProlognOffering",
  async (params: {
    serviceId: number;
    prolongTypeId: number;
    offeringId: number;
    activationCheck?: boolean;
  }) => {
    try {
      const response = axios.patch(changeProlognOfferingRoute(params));
      const { data } = await response;
      Modal.success({ title: "Условия пролонгации изменены" });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения условий пролонгации",
      });
    }
  }
);

// Получение статуса блокировки продукта
export const fetchProductsBlockingTypeStatus = createAsyncThunk(
  "products/fetchProductsBlockingTypeStatus",
  async (params: IProductsBlockingTypeStatus) => {
    try {
      const url = getProductsBlockingTypeStatusRoute(params);
      const { data } = await axios(url);
      return data;
    } catch (e) {
      console.log(e);
    }
  }
);

export const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    cleanProduct: (state) => {
      state.productLoading = "idle";
      state.product = undefined;
    },
    cleanProductList: (state) => {
      state.productsListLoading = "idle";
      state.productsList = [];
    },
    cleanProductversions: (state) => {
      state.productVersionsLoading = "idle";
      state.productVersions = [];
    },
    cleanProlongOffering: (state) => {
      state.prolongOfferingLoading = "idle";
      state.prolongOffering = undefined;
    },
    cleanProlongOfferingVersions: (state) => {
      state.prolongOfferingVersionsLoading = "idle";
      state.prolongOfferingVersions = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeProlognOffering.pending, (state) => {
        state.changeProductLoading = "pending";
      })
      .addCase(changeProlognOffering.rejected, (state) => {
        state.changeProductLoading = "rejected";
      })
      .addCase(changeProlognOffering.fulfilled, (state, { payload }) => {
        state.changeProductLoading = "fulfilled";
      })
      .addCase(closeProduct.pending, (state) => {
        state.changeProductLoading = "pending";
      })
      .addCase(closeProduct.rejected, (state) => {
        state.changeProductLoading = "rejected";
      })
      .addCase(closeProduct.fulfilled, (state, { payload }) => {
        state.changeProductLoading = "fulfilled";
      })
      .addCase(changeProductStatus.pending, (state) => {
        state.changeProductLoading = "pending";
      })
      .addCase(changeProductStatus.rejected, (state) => {
        state.changeProductLoading = "rejected";
      })
      .addCase(changeProductStatus.fulfilled, (state, { payload }) => {
        state.changeProductLoading = "fulfilled";
      })
      .addCase(changeProductQuantity.pending, (state) => {
        state.changeProductQuantityLoading = "pending";
      })
      .addCase(changeProductQuantity.rejected, (state) => {
        state.changeProductQuantityLoading = "rejected";
      })
      .addCase(changeProductQuantity.fulfilled, (state, { payload }) => {
        state.changeProductQuantityLoading = "fulfilled";
      })
      .addCase(cancelProduct.pending, (state) => {
        state.changeProductLoading = "pending";
      })
      .addCase(cancelProduct.rejected, (state) => {
        state.changeProductLoading = "rejected";
      })
      .addCase(cancelProduct.fulfilled, (state, { payload }) => {
        state.changeProductLoading = "fulfilled";
      })
      .addCase(activateProduct.pending, (state) => {
        state.changeProductLoading = "pending";
      })
      .addCase(activateProduct.rejected, (state) => {
        state.changeProductLoading = "rejected";
      })
      .addCase(activateProduct.fulfilled, (state, { payload }) => {
        state.changeProductLoading = "fulfilled";
      })
      .addCase(fetchProductById.pending, (state) => {
        state.productLoading = "pending";
      })
      .addCase(fetchProductById.rejected, (state) => {
        state.productLoading = "rejected";
        state.product = undefined;
      })
      .addCase(fetchProductById.fulfilled, (state, { payload }) => {
        state.productLoading = "fulfilled";
        state.product = payload;
      })
      .addCase(fetchProlongOffering.pending, (state) => {
        state.prolongOfferingLoading = "pending";
      })
      .addCase(fetchProlongOffering.rejected, (state) => {
        state.prolongOfferingLoading = "rejected";
        state.prolongOffering = undefined;
      })
      .addCase(fetchProlongOffering.fulfilled, (state, { payload }) => {
        state.prolongOfferingLoading = "fulfilled";
        state.prolongOffering = payload;
      })
      .addCase(fetchProlongOfferingVersions.pending, (state) => {
        state.prolongOfferingVersionsLoading = "pending";
      })
      .addCase(fetchProlongOfferingVersions.rejected, (state) => {
        state.prolongOfferingVersionsLoading = "rejected";
        state.prolongOfferingVersions = {};
      })
      .addCase(fetchProlongOfferingVersions.fulfilled, (state, { payload }) => {
        state.prolongOfferingVersionsLoading = "fulfilled";
        state.prolongOfferingVersions = payload;
      })
      .addCase(fetchProductVersions.pending, (state) => {
        state.productVersionsLoading = "pending";
      })
      .addCase(fetchProductVersions.rejected, (state) => {
        state.productVersionsLoading = "rejected";
        state.productVersions = [];
      })
      .addCase(fetchProductVersions.fulfilled, (state, { payload }) => {
        state.productVersionsLoading = "fulfilled";
        if (Array.isArray(payload)) {
          // Сорт нужен так как не стабильно приходит с бэка сортировка версий изначальная
          state.productVersions = [...payload].sort((recordA, recordB) =>
            moment(recordA.fromDate).isBefore(moment(recordB.fromDate)) ? -1 : 1
          );
        }
      })
      .addCase(fetchProductsByAggId.pending, (state) => {
        state.productsListLoading = "pending";
      })
      .addCase(fetchProductsByAggId.rejected, (state) => {
        state.productsListLoading = "rejected";
        state.productsList = [];
      })
      .addCase(fetchProductsByAggId.fulfilled, (state, { payload }) => {
        state.productsListLoading = "fulfilled";
        state.productsList = payload;
      })
      .addCase(fetchHierarchyProductById.pending, (state) => {
        state.productHierarchyLoading = "pending";
      })
      .addCase(fetchHierarchyProductById.rejected, (state) => {
        state.productHierarchyLoading = "rejected";
        state.productHierarchy = undefined;
      })
      .addCase(fetchHierarchyProductById.fulfilled, (state, { payload }) => {
        state.productHierarchyLoading = "fulfilled";
        state.productHierarchy = payload;
      })
      .addCase(fetchProductsBlockingTypeStatus.fulfilled, (state, { payload }) => {
        state.productBlockingStatus = payload;
      });
  },
});

export const { reducer: productsReducer, actions: productsActions } = productsSlice;
